import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { SNEtaWindow, SNTicketEventAction } from '@nsc/servicenow/types.js';
import { ServiceNowTicket } from '../db/ServiceNowTicket.js';
import { assertAssignedFE, snAssignedTo } from './_assignee-guard.js';
import { buildCommentPayload } from './_comment-payload.js';
import { clearPending, setPending } from './_pending-ops.js';
import { assertProblemReportEnabled } from './_problem-reports.js';
import { publishTicketChanged } from './_ticket-events.js';

const ETA_WINDOWS: ReadonlySet<SNEtaWindow> = new Set(['early_morning', 'morning', 'midday', 'afternoon', 'evening']);

// Worknotes-only / "On Hold" actions that move neither state nor substate: we don't poll or raise
// the overlay for them (polling would just time out). The polled actions — intervention_accepted
// (16→17), en_route (substate ∅→8), intervention_started (17→18), intervention_finished (18→3),
// cancellation_requested (→7) — are deliberately absent.
const NON_STATE_CHANGING_ACTIONS: ReadonlySet<string> = new Set([
  'eta_window',
  'attempted_contact',
  'parts_claimed',
  'parts_returned_to_pudo',
  'parts_not_dispatched',
  'customer_requested_reschedule',
  'access_not_granted',
  'unsuccessful_visit',
  'other_problem',
  'add_worknote',
]);

const POLL_INTERVAL = 500;
const MAX_POLLS = 30;

/**
 * Core helper: ensure the caller is the assigned FE for the ticket, then post the event.
 *
 * When ServiceNow returns 202 (Accepted) the action is being processed asynchronously.
 * For state-changing actions we poll `getTicketDetails` every 500ms until the state
 * transitions or we hit a 15s timeout. The in-memory pending-ops store keeps the ticket
 * frozen on page reloads while SN catches up.
 */
async function submitTicketAction(
  ticketNumber: string,
  action: SNTicketEventAction,
  payload?: Record<string, unknown>,
  eventTime?: Date,
): Promise<ServiceNowTicket> {
  const { getTicketDetails, postTicketEvent } = await import('@nsc/servicenow/smartteam-api.js');
  const sn = await getTicketDetails(ticketNumber);
  await assertAssignedFE(snAssignedTo(sn)?.value);
  await assertProblemReportEnabled(action, sn.customer?.reference?.value);

  const preState = sn.state?.value ?? '';
  const preSubstate = sn.substate?.value ?? '';
  const { status } = await postTicketEvent({ ticketId: ticketNumber, action, payload, eventTime });

  // Nudge anyone else viewing this ticket (customer, admin) to re-read live SN state — same
  // channel the inbound webhook uses. The inbound webhook fires a second nudge once SN has
  // settled the change; the initiating FE refreshes via runAction.
  publishTicketChanged(ticketNumber);

  if (status === 202 && !NON_STATE_CHANGING_ACTIONS.has(action)) {
    setPending(ticketNumber, action, preState, preSubstate);

    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
      try {
        const updated = await getTicketDetails(ticketNumber);
        const newState = updated.state?.value ?? '';
        const newSubstate = updated.substate?.value ?? '';
        // Done on a state move, or a forward substate move (en_route ∅→8). Require non-empty
        // newSubstate so a transient clear doesn't read as "done" before the real change lands.
        if (newState !== preState || (newSubstate !== '' && newSubstate !== preSubstate)) {
          clearPending(ticketNumber);
          return new ServiceNowTicket({
            number: updated.number?.display_value || ticketNumber,
            state: updated.state?.value || undefined,
            stateLabel: updated.state?.display_value || undefined,
            substate: updated.substate?.value || undefined,
            substateLabel: updated.substate?.display_value || undefined,
          });
        }
      } catch (err) {
        logger.warn({ ticketNumber, action, poll: i + 1, err }, 'Poll iteration failed');
      }
    }

    // Still pending after the timeout — leave the op set; getTicketByNumber / the webhook / the 60s
    // TTL release it once SN advances, so the overlay self-heals on the next read.
    logger.warn({ ticketNumber, action, polls: MAX_POLLS }, 'Polling timed out — ticket still pending');
    return new ServiceNowTicket({
      number: sn.number?.display_value || ticketNumber,
      state: sn.state?.value || undefined,
      stateLabel: sn.state?.display_value || undefined,
      substate: sn.substate?.value || undefined,
      substateLabel: sn.substate?.display_value || undefined,
      pendingAction: action,
    });
  }

  // Synchronous completion (200) or any other non-202 success: one refetch for the
  // post-action state. A non-state-changing 202 skips this and returns the snapshot below.
  if (status !== 202) {
    try {
      const updated = await getTicketDetails(ticketNumber);
      return new ServiceNowTicket({
        number: updated.number?.display_value || ticketNumber,
        state: updated.state?.value || undefined,
        stateLabel: updated.state?.display_value || undefined,
        substate: updated.substate?.value || undefined,
        substateLabel: updated.substate?.display_value || undefined,
      });
    } catch (err) {
      logger.warn({ ticketNumber, action, err }, 'Post-action refetch failed — returning pre-action snapshot');
    }
  }

  return new ServiceNowTicket({
    number: sn.number?.display_value || ticketNumber,
    state: sn.state?.value || undefined,
    stateLabel: sn.state?.display_value || undefined,
    substate: sn.substate?.value || undefined,
    substateLabel: sn.substate?.display_value || undefined,
  });
}

/** Parse a backdated `yyyy-mm-dd hh:mm:ss` GMT timestamp to a Date, or `undefined` if blank. */
function parseAt(at: string | undefined): Date | undefined {
  if (!at) return undefined;
  const d = new Date(at.replace(' ', 'T') + 'Z');
  return Number.isNaN(d.getTime()) ? undefined : d;
}

// ── Core flow ───────────────────────────────────────────────────────

/**
 * Explicit acceptance of the assigned WOT (spec §4: `intervention_accepted`).
 * Drives the state transition Assigned (16) → Accepted (17). The integration
 * partner relies on this as a discrete event — silently moving via `en_route`
 * isn't supported.
 */
export const ticketActionInterventionAccepted = registerMutation({
  operationName: 'ticketActionInterventionAccepted',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    { name: 'at', type: () => String },
  ],
  resolve: (ticketNumber: string, at?: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'intervention_accepted', undefined, parseAt(at));
  },
});

/**
 * Informational ETA window broadcast to the customer (spec §4: `eta_window`).
 * Categorical, not a timestamp — one of the five buckets in `ETA_WINDOWS`. Fires
 * independently from `en_route`; the FE typically picks the bucket when they
 * know roughly when they'll arrive, well before they actually leave.
 */
export const ticketActionEtaWindow = registerMutation({
  operationName: 'ticketActionEtaWindow',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** One of `early_morning` | `morning` | `midday` | `afternoon` | `evening`. */
    { name: 'window', type: () => String },
  ],
  resolve: (ticketNumber: string, window: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    if (!ETA_WINDOWS.has(window as SNEtaWindow)) {
      throw new Error(`Invalid ETA window: ${window}`);
    }
    return submitTicketAction(ticketNumber, 'eta_window', { window });
  },
});

export const ticketActionEnRoute = registerMutation({
  operationName: 'ticketActionEnRoute',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** Expected arrival timestamp, `yyyy-mm-dd hh:mm:ss` GMT (spec §4 `payload.eta`). */
    { name: 'eta', type: () => String },
    /** Optional FE-supplied event time, `yyyy-mm-dd hh:mm:ss` GMT. Defaults to now. */
    { name: 'at', type: () => String },
  ],
  resolve: (ticketNumber: string, eta: string, at?: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    // Spec §4 en_route payload carries both the actual departure (`travel_start`) and the
    // estimated arrival (`eta`). `travel_start` mirrors the FE-supplied event time `at` (already
    // a `yyyy-mm-dd hh:mm:ss` GMT string), which also drives the envelope `event_time`; it's
    // dropped when the FE didn't supply a time, exactly like the optional reschedule fields.
    const payload: Record<string, unknown> = at ? { travel_start: at, eta } : { eta };
    return submitTicketAction(ticketNumber, 'en_route', payload, parseAt(at));
  },
});

export const ticketActionInterventionStarted = registerMutation({
  operationName: 'ticketActionInterventionStarted',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    { name: 'at', type: () => String },
  ],
  resolve: (ticketNumber: string, at?: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'intervention_started', undefined, parseAt(at));
  },
});

export const ticketActionInterventionFinished = registerMutation({
  operationName: 'ticketActionInterventionFinished',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    { name: 'at', type: () => String },
  ],
  resolve: (ticketNumber: string, at?: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'intervention_finished', undefined, parseAt(at));
  },
});

// ── Parts handling ──────────────────────────────────────────────────
// "Part exchange between two FEs" isn't a single spec action — modelled here as two
// distinct steps: FE-A drops at the PUDO (`parts_returned_to_pudo`), then FE-B picks
// up (`parts_claimed`).

export const ticketActionPartsClaimed = registerMutation({
  operationName: 'ticketActionPartsClaimed',
  type: () => ServiceNowTicket,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'parts_claimed');
  },
});

export const ticketActionPartsReturnedToPudo = registerMutation({
  operationName: 'ticketActionPartsReturnedToPudo',
  type: () => ServiceNowTicket,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'parts_returned_to_pudo');
  },
});

export const ticketActionPartsNotDispatched = registerMutation({
  operationName: 'ticketActionPartsNotDispatched',
  type: () => ServiceNowTicket,
  params: [{ name: 'ticketNumber', type: () => String }],
  resolve: (ticketNumber: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'parts_not_dispatched');
  },
});

// ── Reschedule / contact / access exceptions ────────────────────────

/**
 * Spec §4 splits the `customer_requested_reschedule` payload by when it fires:
 *   - Before en-route: only `agreed_date` (DELL: CAD C01).
 *   - After en-route:  `agreed_date` + `new_primary_contact` + `new_contact_phone` (DELL: CAD S01).
 * The caller decides which form to submit by passing or omitting the contact fields.
 * Empty/undefined contact fields are dropped from the payload so the partner's
 * SyncBridge picks the right DELL code based on what's present.
 *
 * `reason` carries optional free-text FE notes for the service desk (Cegeka confirmed
 * `payload.reason` is the designated slot for this); dropped when empty.
 *
 * `confirmedByName` / `confirmedByPhone` record who on the customer side confirmed the
 * new date — at least one is mandatory so the service desk can verify the reschedule
 * without calling the FE first. Sent as `confirmed_by_name` / `confirmed_by_phone`,
 * deliberately separate from `new_primary_contact` / `new_contact_phone` so the
 * C01/S01 selection (based on the contact fields' presence) stays untouched.
 */
export const ticketActionCustomerRequestedReschedule = registerMutation({
  operationName: 'ticketActionCustomerRequestedReschedule',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** ISO date `yyyy-mm-dd`. */
    { name: 'agreedDate', type: () => String },
    { name: 'newPrimaryContact', type: () => String },
    { name: 'newContactPhone', type: () => String },
    { name: 'reason', type: () => String },
    { name: 'confirmedByName', type: () => String },
    { name: 'confirmedByPhone', type: () => String },
  ],
  resolve:
    (
      ticketNumber: string,
      agreedDate: string,
      newPrimaryContact?: string,
      newContactPhone?: string,
      reason?: string,
      confirmedByName?: string,
      confirmedByPhone?: string,
    ) =>
    async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
      const byName = confirmedByName?.trim();
      const byPhone = confirmedByPhone?.trim();
      if (!byName && !byPhone) {
        throw new Error('Name or phone number of the customer who confirmed the new date is required');
      }
      const payload: Record<string, unknown> = { agreed_date: agreedDate };
      if (byName) payload.confirmed_by_name = byName;
      if (byPhone) payload.confirmed_by_phone = byPhone;
      if (newPrimaryContact) payload.new_primary_contact = newPrimaryContact;
      if (newContactPhone) payload.new_contact_phone = newContactPhone;
      if (reason?.trim()) payload.reason = reason.trim();
      return submitTicketAction(ticketNumber, 'customer_requested_reschedule', payload);
    },
});

export const ticketActionAccessNotGranted = registerMutation({
  operationName: 'ticketActionAccessNotGranted',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** Spec §4 example uses `SECURED_SITE`; passed through verbatim. */
    { name: 'reason', type: () => String },
    /**
     * Optional free-text FE notes for the service desk. Appended after the reason code
     * (`SECURED_SITE: <notes>`) so the code prefix stays machine-readable while the desk
     * still gets the human context in `payload.reason` (per Cegeka).
     */
    { name: 'notes', type: () => String },
  ],
  resolve:
    (ticketNumber: string, reason: string, notes?: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
      const fullReason = notes?.trim() ? `${reason}: ${notes.trim()}` : reason;
      return submitTicketAction(ticketNumber, 'access_not_granted', { reason: fullReason });
    },
});

export const ticketActionUnsuccessfulVisit = registerMutation({
  operationName: 'ticketActionUnsuccessfulVisit',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** Currently always `JUST_GO` per spec §4. */
    { name: 'visitType', type: () => String },
    /** Optional free-text FE notes for the service desk (`payload.reason`, per Cegeka). */
    { name: 'reason', type: () => String },
  ],
  resolve:
    (ticketNumber: string, visitType: string, reason?: string) =>
    async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
      const payload: Record<string, unknown> = { visit_type: visitType };
      if (reason?.trim()) payload.reason = reason.trim();
      return submitTicketAction(ticketNumber, 'unsuccessful_visit', payload);
    },
});

export const ticketActionAttemptedContact = registerMutation({
  operationName: 'ticketActionAttemptedContact',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    { name: 'attemptsByEngineer', type: () => Int },
    /** Spec §4 example uses `NO_ANSWER`. */
    { name: 'result', type: () => String },
    /** Optional free-text FE notes for the service desk (`payload.reason`, per Cegeka). */
    { name: 'reason', type: () => String },
    /** Phone numbers the FE called — mandatory, at least one (`payload.called_numbers`). */
    { name: 'calledNumbers', type: () => [String] },
    /**
     * When the (last) call was made, `yyyy-mm-dd hh:mm:ss` GMT — mandatory. Sent as
     * `payload.call_time` and mirrored into the envelope `event_time`, like `en_route`
     * does with `travel_start`.
     */
    { name: 'callTime', type: () => String },
  ],
  resolve:
    (
      ticketNumber: string,
      attemptsByEngineer: number,
      result: string,
      reason?: string,
      calledNumbers?: string[],
      callTime?: string,
    ) =>
    async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
      const numbers = (calledNumbers ?? []).map(n => n.trim()).filter(Boolean);
      if (!numbers.length) {
        throw new Error('At least one called phone number is required');
      }
      const callTimeDate = parseAt(callTime);
      if (!callTimeDate) {
        throw new Error('A valid call time is required');
      }
      const payload: Record<string, unknown> = {
        attempts_by_engineer: attemptsByEngineer,
        result,
        called_numbers: numbers,
        call_time: callTime,
      };
      if (reason?.trim()) payload.reason = reason.trim();
      return submitTicketAction(ticketNumber, 'attempted_contact', payload, callTimeDate);
    },
});

/**
 * Generic "other problem" report (spec §4.3 `other_problem`; DELL replycode TPM [E10]).
 * Surfaces in the app as the "TPM" Report-a-problem button. The FE's mandatory free-text is
 * the whole report — sent verbatim as `payload.reason` (mapped to `wm_order.u_response_message`).
 * Like the other On-Hold exceptions it parks the task (substate Paused) and raises
 * needs-attention without moving `state`, so it's treated as non-state-changing (no polling).
 */
export const ticketActionOtherProblem = registerMutation({
  operationName: 'ticketActionOtherProblem',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** Mandatory free-text problem description — the whole TPM report. */
    { name: 'reason', type: () => String },
  ],
  resolve: (ticketNumber: string, reason: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    const text = reason?.trim();
    if (!text) {
      throw new Error('A problem description is required');
    }
    return submitTicketAction(ticketNumber, 'other_problem', { reason: text });
  },
});

export const ticketActionCancellationRequested = registerMutation({
  operationName: 'ticketActionCancellationRequested',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    /** Spec §4 example uses `ENGINEER_REQUEST`. */
    { name: 'reason', type: () => String },
  ],
  resolve: (ticketNumber: string, reason: string) => async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
    return submitTicketAction(ticketNumber, 'cancellation_requested', { reason });
  },
});

/**
 * General FE comment → a ServiceNow **work note** on the work order task (spec `add_worknote`, mapped to
 * `wm_task.work_notes`; internal only). The text the engineer typed is the note, so it shows on the task
 * and comes back in `ticket_details.work_notes` (and in the app's own notes block).
 *
 * This used to be `add_handling_note`, which creates a *special handling note* (`sn_shn_notes`) on the
 * work order, not on the task, and requires an `effective_end` the app never sent. ServiceNow still
 * answered 202, nothing landed on the task, and a "Chloe Unassigned engineer" work note appeared three
 * seconds later (observed on WOT0015351, 2026-10-05).
 *
 * "Emergency" is carried as an `[EMERGENCY]` prefix: a work note has no priority field. No state change.
 */
export const ticketActionAddComment = registerMutation({
  operationName: 'ticketActionAddComment',
  type: () => ServiceNowTicket,
  params: [
    { name: 'ticketNumber', type: () => String },
    { name: 'comment', type: () => String },
    { name: 'emergency', type: () => Boolean },
  ],
  resolve:
    (ticketNumber: string, comment: string, emergency?: boolean) =>
    async (_gql: BaseQLSelectionSet<ServiceNowTicket>) => {
      return submitTicketAction(ticketNumber, 'add_worknote', buildCommentPayload(comment ?? '', emergency));
    },
});
